import * as R from 'ramda'
import { v4 as uuidV4 } from 'uuid'
import { ErrorMessages } from '@weblite/ws-client'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import {
  CategoryType,
  DeleteMessages,
  EditMessage,
  EventDeleteMessagesPayload,
  EventEditMessagePayload,
  ForwardMessages,
  ForwardMessagesPayload,
  GetMessages,
  GetMessagesOfPublicHall,
  GetMessagesWithCategoryAndRange,
  IMessage,
  Message,
  MessageDestinations,
  MessageRange,
  MessageSender,
  RawContactMessage,
  RawTextMessage,
  RawWappMessage,
  reqDeleteMessages,
  reqEditMessage,
  ReqEditMessagePayload,
  reqForwardMessages,
  reqGetMessages,
  reqGetMessagesOfPublicHall,
  reqGetMessagesWithCategoryAndRange,
  reqSearchMessages,
  reqSendMessageToHall,
  reqSendMessageToPrivateSpace,
  reqSendMessageToUser,
  reqSyncMessages,
  SEARCH_TAG_LINK,
  SearchMessages,
  SendMessagePayload,
  SendMessageToHall,
  SendMessageToPrivateSpace,
  SendMessageToUser,
  SyncMessages,
  WithBodyMessage,
} from '@weblite/ts-action/message'
import { getFileUrl, openFile } from '@weblite/file'
import { Room, RoomTypes } from '@weblite/ts-action/room'
import { User } from '@weblite/ts-action/user'
import { Voice } from '@weblite/voice-recorder'
import * as linkify from 'linkifyjs'
import { CustomAction } from '@weblite-wapps/protocol'
import { getWappSessionByWappIdAndWisId } from 'weblite-web-wapprunner'
import { request, requestNow } from '$setup/ws'
import * as roomService from '$logic/room'
import {
  getRoom,
  getRoomFromRedux,
  getStorageByRoomTypeAndRoomId,
  isClientsRoom,
  isHall,
} from '$logic/room'
import {
  PendingUpload,
  SendMessagePayloadWithPendingId,
} from '$logic/pending-uploads/types'
import { getClientUser, getClientUserId } from '$logic/client-user'
import {
  createPendingUpload,
  deletePendingUploads,
  syncPendingUploads,
} from '$logic/pending-uploads'
import { getPv, getPvByUserId, getUserIdsByPvIds } from '$logic/pv'
import {
  createPendingMessages,
  deletePendingMessages,
  getAllPendingMessages,
} from '$logic/pending-messages'
import { PendingMessage } from '$logic/pending-messages/types'
import * as openedRoomMessagesService from '$logic/opened-room/opened-room-messages.service'
import * as pinMessagesService from '$logic/pin'
import { getWithPinHistoryRoomIds } from '$logic/pin'
import {
  getOpenedRoomId,
  openedRoomPermissions,
  openRoom,
} from '$logic/opened-room'
import { getHall, isPublicHall } from '$logic/hall'
import { getUser, getUserDisplayName, getUserFirstName } from '$logic/users'
import { getImageUrl } from '$func/imageUrl'
import { IS_IOS } from '$func/environmentInfo'
import { ImageDetails } from '$comp/modals/image-viewer'
import { closeForwardList, openForwardList } from '$logic/router'
import { sendFieldApis } from '$comp/_app/components/chat/components/footer/components/send-field'
import {
  showDeleteContentFromInlineConfirmModal,
  showDeleteMessageModal,
} from '$comp/modals/confirmation-dialog'
import { messageTypeByFileType } from '$func/file'
import {
  messageLatestUpdateTimeStorage,
  messagesStorage,
} from '$logic/message/storage'
import * as sharedMediaService from '$logic/shared-media/service'

import { decodeSubscriptionPermissions } from '$logic/subscription'
import {
  calculateMissingMessageRange,
  convertVoiceToFile,
  fillDeletedMessages,
  getAudioAttachmentFromFile,
  getFileAttachmentFromFile,
  getImageMessageAttachmentFromFile,
  calculateValidMessageRange,
  getVideoAttachmentFromFile,
  getVoiceAttachmentFromFile,
  groupDeletePatches,
} from '$logic/message/helper'
import { getWapp } from '$logic/wapps/service'
import { handleCommandInMessage } from '$logic/message/plugin'
import {
  updateRoomLastMessageDebounced,
  updateTemporaryLastMessages,
} from '$logic/room-last-message'
import {
  showInviteLinkSentSuccessfullySnackbar,
  showNetworkErrorSnackbar,
  showNMessageForwardedToChatSnackbar,
} from '$comp/snackbars'
import { sendCustomActionToWapp } from '$logic/wapp-communication/helper/wapps.helper'
import { nothing } from '$func/utils'
import { PendingEdit } from '$logic/pending-edits/types'
import {
  createPendingEdits,
  handlePendingEdit,
  syncPendingEdits,
} from '$logic/pending-edits'
import {
  editAudioMessage,
  editFileMessage,
  editImageMessage,
  editVideoMessage,
  syncPendingEditUploads,
} from '$logic/pending-edit-uploads'
import { openedRoomMessagesStorage } from '$logic/opened-room/opened-room-messages.storage'
import { getThumbnailUploadId } from '$logic/pending-uploads/helper'
import { pinRoomApi } from '$comp/_app/components/chat/components/body/components/pin-list/pin-list'
import { onAfterMessagesSentToChat } from '$logic/opened-room/opened-room-messages.service'
import { SharedMediaCategoryType } from '$logic/shared-media/types'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import { asyncDelay } from '$func/delay'
import { t } from '$i18n'
import { loadChatProfiler } from '$comp/_app/components/chat/components/body/components/messageList/react-hooks'
import { RecordedVoice } from '$comp/_app/components/chat/components/footer/components/send-field/components/voice-recorder/types'
import { lastOpenedRoomMessagesService } from '$logic/last-opened-room-messages/service'

function getMessageSenderByRoom(room: Room): MessageSender {
  return room.type === 'CHANNEL'
    ? { type: 'CHANNEL', id: room.id }
    : { type: 'USER', id: getClientUserId() }
}

function convertPendingMessageToSendMessagePayload(
  pendingMessage: PendingMessage,
) {
  return {
    pendingId: pendingMessage.id,
    ...R.omit(
      ['id', 'pendingDate', 'roomId', 'sender', 'roomIid'],
      pendingMessage,
    ),
  } as SendMessagePayload
}

export const FETCH_MESSAGES_WINDOW_SIZE = 40
export const SEARCH_MESSAGE_BUCKET_SIZE = 30
export const MESSAGE_BODY_SIZE = 4096
export const MESSAGE_CAPTION_SIZE = 1024

export async function sendNewMessage(
  file: File | undefined,
  body: string | undefined,
  repliedTo: number | undefined,
  roomId: string,
  chatType: string,
  videoCompressionHeight?: number,
) {
  if (repliedTo) analite('CHAT_MESSAGE_REPLY')
  if (file) {
    const messageType = messageTypeByFileType(file.type)
    if (messageType === 'IMAGE') sendImageMessage(file, body, repliedTo, roomId)
    else if (messageType === 'VIDEO')
      sendVideoMessage(file, body, repliedTo, roomId, videoCompressionHeight)
    else if (messageType === 'FILE')
      sendFileMessage(file, body, repliedTo, roomId)
    else if (messageType === 'AUDIO')
      sendAudioMessage(file, body, repliedTo, roomId)
    analite('CHAT_SEND_MESSAGE', { type: messageType, chatType })
  } else if (body) sendTextMessage(body, repliedTo, roomId, chatType)
}

export async function sendVoiceMessage(
  voice: RecordedVoice,
  caption: string | undefined,
  repliedTo: number | undefined,
  roomId: string,
) {
  const room = await roomService.getRoom(roomId)
  const uploadId = uuidV4()
  const file = convertVoiceToFile(voice)
  const attachment = await getVoiceAttachmentFromFile(file, voice)

  const pendingUpload: PendingUpload = {
    id: uploadId,
    roomId,
    sender: getMessageSenderByRoom(room),
    type: 'VOICE',
    uploadId,
    body: caption,
    attachment,
    repliedTo,
    pendingDate: dateWithOffset(),
  }

  createPendingUpload(pendingUpload, file)
}

export async function sendImageMessage(
  file: File,
  caption: string | undefined,
  repliedTo: number | undefined,
  roomId: string,
) {
  const room = await roomService.getRoom(roomId)
  const uploadId = uuidV4()
  const { attachment, image } = await getImageMessageAttachmentFromFile(file)
  const pendingUpload: PendingUpload = {
    id: uploadId,
    roomId,
    sender: getMessageSenderByRoom(room),
    type: 'IMAGE',
    uploadId,
    attachment,
    ...(caption && { body: caption }),
    ...(repliedTo && { repliedTo }),
    pendingDate: dateWithOffset(),
  }
  createPendingUpload(pendingUpload, image)
}

export async function sendVideoMessage(
  file: File,
  caption: string | undefined,
  repliedTo: number | undefined,
  roomId: string,
  compressionHeight?: number,
) {
  const room = await roomService.getRoom(roomId)
  const uploadId = uuidV4()
  const thumbnailUploadId = getThumbnailUploadId(uploadId)

  const { pendingAttachment, videoHighQualityThumbnail } =
    await getVideoAttachmentFromFile(file, compressionHeight)

  const pendingUpload: PendingUpload = {
    id: uploadId,
    roomId,
    sender: getMessageSenderByRoom(room),
    type: 'VIDEO',
    uploadId,
    attachment: pendingAttachment,
    ...(caption && { body: caption }),
    ...(repliedTo && { repliedTo }),
    pendingDate: dateWithOffset(),
    thumbnailUploadId,
  }
  createPendingUpload(pendingUpload, file, videoHighQualityThumbnail)
}

export async function editMessageHandler(
  roomId: string,
  iid: number,
  messageType: IMessage['type'],
  body?: string,
  file?: File,
  compressionHeight?: number,
) {
  if (messageType === 'TEXT' && !body) return
  if (messageType === 'VOICE' && !body) return

  analite('CHAT_MESSAGE_EDIT')
  if (file) {
    if (messageType === 'IMAGE') editImageMessage(roomId, iid, file, body)
    else if (messageType === 'VIDEO') {
      editVideoMessage(roomId, iid, file, compressionHeight, body)
    } else if (messageType === 'FILE') {
      editFileMessage(roomId, iid, file, body)
    } else if (messageType === 'AUDIO') {
      editAudioMessage(roomId, iid, file, body)
    }
  } else if (body) {
    editTextMessage(roomId, iid, body)
  }
}

export async function sendAudioMessage(
  file: File,
  caption: string | undefined,
  repliedTo: number | undefined,
  roomId: string,
) {
  const room = await roomService.getRoom(roomId)
  const uploadId = uuidV4()
  const pendingAttachment = await getAudioAttachmentFromFile(file)

  const pendingUpload: PendingUpload = {
    id: uploadId,
    roomId,
    sender: getMessageSenderByRoom(room),
    type: 'AUDIO',
    uploadId,
    attachment: pendingAttachment,
    pendingDate: dateWithOffset(),
    ...(caption && { body: caption }),
    ...(repliedTo && { repliedTo }),
  }

  createPendingUpload(pendingUpload, file)
}

export async function sendFileMessage(
  file: File,
  caption: string | undefined,
  repliedTo: number | undefined,
  roomId: string,
) {
  const room = await roomService.getRoom(roomId)
  const uploadId = uuidV4()

  const pendingAttachment = getFileAttachmentFromFile(file)

  const pendingUpload: PendingUpload = {
    id: uploadId,
    roomId,
    sender: getMessageSenderByRoom(room),
    type: 'FILE',
    uploadId,
    attachment: pendingAttachment,
    pendingDate: dateWithOffset(),
    ...(caption && { body: caption }),
    ...(repliedTo && { repliedTo }),
  }
  createPendingUpload(pendingUpload, file)
}

export async function sendTextMessage(
  body: string,
  repliedTo: number | undefined,
  roomId: string,
  chatType: string,
) {
  const handled = await handleCommandInMessage(body, roomId, chatType)
  if (handled) return

  const messages: (RawTextMessage & { repliedTo: number | undefined })[] =
    R.splitEvery(MESSAGE_BODY_SIZE, body).map(splittedCaption => ({
      body: splittedCaption,
      repliedTo,
      type: 'TEXT',
    }))
  onAfterMessagesSentToChat()
  analite('CHAT_SEND_MESSAGE', { type: 'TEXT', chatType })
  await sendMessage(roomId, messages)
}

export async function sendContactMessage(roomId: string, userId: string) {
  const { firstname, lastname, phone, id } = await getUser(userId)
  const room = await getRoom(roomId)
  const message: RawContactMessage = {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attachment: { firstname, lastname, phone, id },
    type: 'CONTACT',
  }
  onAfterMessagesSentToChat()
  analite('CHAT_SEND_MESSAGE', { type: 'CONTACT', chatType: room?.type })
  await sendMessage(roomId, [message])
}

export const getMessageLinks = (body: string) => linkify.find(body || '')

export async function sendMessage(
  roomId: string,
  messages: SendMessagePayloadWithPendingId[],
) {
  const room = await roomService.getRoom(roomId)
  const pendingMessages = messages.map(sendMessagePayload => ({
    ...sendMessagePayload,
    links:
      'body' in sendMessagePayload
        ? getMessageLinks(sendMessagePayload.body).map(({ value }) => ({
            url: value,
          }))
        : undefined,
    roomId,
    sender: getMessageSenderByRoom(room),
    pendingDate: dateWithOffset(),
    id: 'id' in sendMessagePayload ? sendMessagePayload.id : uuidV4(),
    roomIid: room.iid,
  }))
  await createPendingMessages(pendingMessages)
  deletePendingUploads(messages.map(msg => msg.id).filter(Boolean))
  await sendPendingMessages()
}

export function getPvPushData() {
  const user = getClientUser()
  const userFullName = getUserDisplayName(user as User)
  return { title: userFullName, icon: getImageUrl(user.image) }
}

export async function getHallPushData(roomId: string) {
  const room = await getRoom(roomId)
  const hall = await getHall(roomId)

  return {
    title: hall.name,
    icon: getImageUrl(hall.image),
    ...(room.type === 'GROUP' && {
      senderName: getUserDisplayName(getClientUser() as User),
    }),
  }
}

async function sendMessageRequestByRoomId(
  roomId: string,
  messages: SendMessagePayload[],
) {
  const room = await roomService.getRoom(roomId)

  if (room?.type === 'PV') {
    const { to } = await getPv(roomId)
    return requestNow<SendMessageToUser>(
      reqSendMessageToUser({ userId: to, messages, push: getPvPushData() }),
    )
  }
  if (room?.type === 'CHANNEL' || room?.type === 'GROUP') {
    const push = await getHallPushData(roomId)
    return requestNow<SendMessageToHall>(
      reqSendMessageToHall({ hallId: roomId, messages, push }),
    )
  }
  if (room?.type === 'PRIVATE_SPACE')
    return requestNow<SendMessageToPrivateSpace>(
      reqSendMessageToPrivateSpace({ messages }),
    )
  //  TODO: Show error
  return null
}

export async function sendWappMessage(
  roomId: string,
  attachment: RawWappMessage['attachment'],
  roomType: string,
) {
  const { name, width, height } = await getWapp(attachment.wappId)
  const repliedTo = sendFieldApis.getRepliedTo()
  const message: RawWappMessage & { repliedTo: number } = {
    type: 'WAPP',
    repliedTo,
    attachment: { width, height, ...attachment },
  }
  onAfterMessagesSentToChat()
  await sendMessage(roomId, [message])
  if (repliedTo) sendFieldApis.reset()
  analite('CHAT_SEND_WAPP', { wappName: name, chatType: roomType })
}

export async function editTextMessage(
  roomId: string,
  iid: number,
  body?: string,
) {
  const message = await getMessageByIid(roomId, iid)
  const pendingEdit = {
    id: message.id,
    roomId: message.roomId,
    iid: message.iid,
    body,
  } as PendingEdit
  await createPendingEdits([pendingEdit])
  await handlePendingEdit(pendingEdit)
}

export async function editMessage(patch: ReqEditMessagePayload) {
  const patchWithLinks = {
    ...patch,
    links:
      'body' in patch
        ? getMessageLinks(patch.body).map(({ value }) => ({ url: value }))
        : undefined,
  }
  // TODO Change message status to pending
  const res = await request<EditMessage>(reqEditMessage(patchWithLinks))
  if (res.error) {
    // TODO: Display proper alert
  }
  await saveEditMessage([patchWithLinks])
}

export async function deleteMessages(roomId: string, iids: number[]) {
  // TODO optimistic: shouldn't delete from client before request?
  const patch = { roomId, iids }
  const res = await request<DeleteMessages>(reqDeleteMessages(patch))
  if (res.error) {
    //   TODO: Display proper alert
  }
  await deleteMessagesFromClient(patch)
  await roomService.handleDeletedMessages(roomId, iids)
}

async function roomIdsToDestinations(
  roomIds: string[],
): Promise<MessageDestinations> {
  const rooms = await Promise.all(roomIds.map(getRoom))

  const groups = R.map(
    R.pluck('id'),
    R.groupBy(R.prop('type'), rooms),
  ) as Record<RoomTypes, string[]>

  const halls = await Promise.all(
    [...(groups.GROUP ?? []), ...(groups.CHANNEL ?? [])].map(async id => ({
      id,
      push: await getHallPushData(id),
    })),
  )

  return {
    halls,
    users: (await getUserIdsByPvIds(groups.PV)).map(id => ({
      id,
      push: getPvPushData(),
    })),
    privateSpace: Boolean(groups.PRIVATE_SPACE),
  }
}

export async function forwardMessages(
  roomIds: string[],
  messages: ForwardMessagesPayload,
  caption?: string,
) {
  const roomsPromise = Promise.all(roomIds.map(getRoom)).then(rooms => {
    rooms.forEach(room => {
      analite('CHAT_SEND_MESSAGE', {
        type: 'FORWARD',
        chatType: room?.type,
        count: messages.iids.length,
      })
    })
    return rooms
  })

  if (caption)
    await Promise.all(
      roomIds.map(async (roomId, index) => {
        const rooms = await roomsPromise
        await sendNewMessage(null, caption, null, roomId, rooms[index]?.type)
      }),
    )

  const publicHall = await isPublicHall(messages.roomId)
  // @market: Maybe we should save pending messages.
  const res = await request<ForwardMessages>(
    reqForwardMessages({
      messages: { ...messages, publicHall },
      destinations: await roomIdsToDestinations(roomIds),
    }),
  )
  if (res.error) {
    // TODO: Display proper alert
  }

  onAfterMessagesSentToChat(true, res.payload.messages)
  await handleNewMessage(res.payload.messages)
}

async function getNonEmptyRoomIds() {
  // TODO #optimize use better algorithm b/c it's sorted OR cache?
  return R.uniq(
    R.pluck(0, await messagesStorage.findMany(undefined, { keyOnly: true })),
  ) as string[]
}
export function decodePendingId(msg: Message) {
  return msg.id.split('_')[0]
}

async function removeSentPendingMessages() {
  // NOTE: Send pending messages to server
  const groupedPendingMessages = R.groupBy(
    R.prop('roomId'),
    getAllPendingMessages(),
  )

  await Promise.all(
    R.toPairs(groupedPendingMessages).map(async ([roomId, pendingMessages]) => {
      if (!isClientsRoom(roomId)) {
        await deletePendingMessages(pendingMessages.map(msg => msg.id))
        return
      }
      const newMessages = await getMessages(roomId, {
        range: [
          pendingMessages.map(msg => msg.roomIid).sort((a, b) => a - b)[0],
          (await getRoom(roomId)).iid,
        ],
      })

      const newMessagesHash = new Set(newMessages.map(decodePendingId))
      const sentPendingMessages = pendingMessages.filter(msg =>
        newMessagesHash.has(msg.id),
      )
      if (sentPendingMessages.length)
        await deletePendingMessages(sentPendingMessages.map(msg => msg.id))
    }),
  )
}

const sendingMessages = new Set<string>()
let messagesAreSynced = false

export function setMessagesAreSynced(val: boolean) {
  messagesAreSynced = val
}

async function sendPendingMessages() {
  if (!messagesAreSynced) return

  const allPendingMessages = getAllPendingMessages()
  const pendingMessages = allPendingMessages.filter(
    message => !sendingMessages.has(message.id),
  )
  pendingMessages.forEach(({ id }) => sendingMessages.add(id))

  const groupedPendingMessages = R.groupBy(R.prop('roomId'), pendingMessages)

  await Promise.all(
    R.toPairs(groupedPendingMessages).map(async ([roomId, roomMessages]) => {
      const messages = R.sortBy(msg => msg.pendingDate, roomMessages).map(
        convertPendingMessageToSendMessagePayload,
      )

      const removeMessagesFromSendingMessages = () => {
        roomMessages.forEach(({ id }) => sendingMessages.delete(id))
      }

      try {
        const res = await sendMessageRequestByRoomId(roomId, messages)
        if (res?.error) showNetworkErrorSnackbar()
        if (res?.payload?.messages) {
          sendMessagesAnalite(res.payload.messages)
          await handleNewMessage(res.payload.messages)

          // TODO: Check this @javad

          // res.payload.messages?.map(updateTemporaryLastMessage)
          updateTemporaryLastMessages(res.payload.messages)
          await deletePendingMessages(res.payload.resolvedIds)
          removeMessagesFromSendingMessages()
        }
      } catch (e: any) {
        if (e instanceof Error && e.message === ErrorMessages.OFFLINE)
          removeMessagesFromSendingMessages()
      }
    }),
  )
}

export async function sendMessagesAnalite(messages: Message[]) {
  const firstMessage = R.head(messages)
  if (!firstMessage || firstMessage.iid > 2) return // Note: between message with iid 1 or 2 always one message is non-event
  const room = await getRoom(firstMessage.roomId)
  if (room.type !== 'PV') return
  if (firstMessage.iid === 1) {
    analite('CREATE_PV')
    return
  }
  const firstPvMessage = await getMessageByIid(firstMessage.roomId, 1)
  if (firstPvMessage.type === 'EVENT') analite('CREATE_PV')
}

export async function syncMessages() {
  const latestUpdateTime = messageLatestUpdateTimeStorage.get()
  if (latestUpdateTime === dateWithOffset()) {
    setMessagesAreSynced(true)
    return
  }

  const [messageRoomIds, pinnedRoomIds] = await Promise.all([
    getNonEmptyRoomIds(),
    getWithPinHistoryRoomIds(),
  ])
  const roomIds = R.uniq(R.concat(messageRoomIds, pinnedRoomIds))

  if (roomIds.length) {
    const res = await request<SyncMessages>(
      reqSyncMessages({ roomIds, latestUpdateTime }),
    )

    if (res.payload)
      await Promise.all([
        saveEditMessage(res.payload.edited),
        ...groupDeletePatches(res.payload.deleted).map(
          deleteMessagesFromClient,
        ),
      ])
  }

  sendingMessages.clear()
  await removeSentPendingMessages()
  setMessagesAreSynced(true)
  await sendPendingMessages()
  await syncPendingUploads()
  await syncPendingEditUploads()
  await syncPendingEdits()
}

async function getValidBoundaries(room: Room) {
  const data = await getStorageByRoomTypeAndRoomId(room.id, room.type).get(
    room.id,
  )
  const upperBound =
    data && 'lastVisibleIid' in data && data.lastVisibleIid
      ? data.lastVisibleIid
      : Infinity
  const lowerBound = data?.firstVisibleIid || 1
  return { lowerBound, upperBound }
}

/**
 * May return more undeleted messages
 */
export async function getMessages(
  roomId: string,
  messageRange: MessageRange,
  onStartFetchingFromServer?: () => void,
) {
  const date = dateWithOffset()
  const room = await getRoom(roomId)

  const validBoundaries = await getValidBoundaries(room)
  const range = calculateValidMessageRange(validBoundaries, messageRange)

  if (!range) return [] as Message[]

  const { messages, complete, undeletedCount } =
    await messagesStorage.getLocalMessages(roomId, range, room.iid)

  if (complete) {
    loadChatProfiler.addTimeoutData({
      [`${date}-getMessages`]: {
        complete,
        undeletedCount,
        range: JSON.stringify(messageRange),
      },
    })
    return messages
  }

  const missingMessageRange = calculateMissingMessageRange(
    range,
    messages,
    undeletedCount,
  )

  loadChatProfiler.addTimeoutData({
    [`${date}-getMessages`]: {
      complete,
      undeletedCount,
      range: JSON.stringify(messageRange),
      missingMessageRange: JSON.stringify(missingMessageRange),
    },
  })

  if ('range' in missingMessageRange) {
    const [start, end] = missingMessageRange.range
    if (end < start) return messages
  } else if (missingMessageRange.count <= 0) {
    return messages
  }

  const isNotJoinedPublicHall =
    ['CHANNEL', 'GROUP'].includes(room.type) &&
    !isClientsRoom(roomId) &&
    (await getHall(roomId)).public

  onStartFetchingFromServer?.()

  loadChatProfiler.addTimeoutData({
    [`${date}-getMessageBeforeRequestToServer`]: true,
  })

  const res = isNotJoinedPublicHall
    ? await request<GetMessagesOfPublicHall>(
        reqGetMessagesOfPublicHall({ roomId, ...missingMessageRange }),
        { retry: -1 },
      )
    : await request<GetMessages>(
        reqGetMessages({ roomId, ...missingMessageRange }),
        { retry: -1 },
      )

  if (res.error) {
    // TODO: Display proper alert
    loadChatProfiler.addTimeoutData({
      [`${date}-requestGetMessagesFromServerError`]: `${res.error.code}-${res.error.message}`,
    })
    return messages
  }

  const gotMessages = res.payload.messages
  loadChatProfiler.addTimeoutData({
    [`${date}-requestGetMessagesFromServerDone`]: `${gotMessages[0]?.iid}-${gotMessages[gotMessages.length - 1]?.iid}`,
  })

  const serverMessages = await fillDeletedMessages(
    res.payload.messages,
    missingMessageRange,
    await roomService.getRoom(roomId),
  )

  loadChatProfiler.addTimeoutData({
    [`${date}-getMessagefillDeletedMessagesDone`]: `${serverMessages[0]?.iid}-${serverMessages[serverMessages.length - 1]?.iid}`,
  })

  saveMessages(serverMessages as Message[]) // TODO

  // TODO #optimize merge-sort
  const uniqMessages = R.uniqBy(R.prop('iid'), [...messages, ...serverMessages])
  const sortedMessages = R.sortBy(R.prop('iid'), uniqMessages) as Message[]

  return sortedMessages.filter(
    ({ iid }) =>
      iid >= validBoundaries.lowerBound && iid <= validBoundaries.upperBound,
  )
}

export async function getMessagesByCategory(
  roomId: string,
  category: SharedMediaCategoryType,
  upperIid: number,
) {
  const res =
    category === 'LINK'
      ? await request<SearchMessages>(
          reqSearchMessages({
            roomId,
            query: '',
            filters: {
              tags: [SEARCH_TAG_LINK],
              iid: { to: upperIid },
            },
            returnComplete: true,
          }),
        )
      : await request<GetMessagesWithCategoryAndRange>(
          reqGetMessagesWithCategoryAndRange({
            category,
            roomId,
            upperIid,
            publicHall: await isPublicHall(roomId),
          }),
        )

  const filteredMessages = res.payload.messages.filter(message =>
    sharedMediaService.filterByCategory(message as IMessage, category),
  ) as Message[]

  await messagesStorage.insert(filteredMessages)
  return {
    messages: filteredMessages,
    fetchedAll:
      filteredMessages.length <
      (category === 'LINK'
        ? FETCH_MESSAGES_WINDOW_SIZE
        : SEARCH_MESSAGE_BUCKET_SIZE),
  }
}

export async function getMessageByIid(
  roomId: string,
  iid: number,
): Promise<Message> {
  const messages = await getMessages(roomId, { range: [iid, iid] })
  if (messages[0]) return messages[0]
  return { iid, roomId, patches: { deleted: true } } as Message
}

export async function getOpenedRoomMessageByIidFromReduxOrIndexedDb(
  iid: number,
): Promise<Message> {
  const roomId = getOpenedRoomId()
  const message = openedRoomMessagesStorage.get(iid)
  if (!openedRoomMessagesStorage.get(iid)) {
    return getMessageByIid(roomId, iid)
  }
  return message
}

export async function handleNewMessage(
  messages: Message[],
  isFromServer = false,
  retryCount = 20,
) {
  const groupedByRoomId = R.groupBy(R.prop('roomId'), messages)

  await Promise.all(
    R.toPairs(groupedByRoomId).map(async ([roomId, roomMessages]) => {
      const roomIid = getRoomFromRedux(roomId)?.iid
      const sortedIid = roomMessages.map(m => m.iid).sort((a, b) => a - b)
      const needsRetry = sortedIid[0] > roomIid + 1
      // Note: in some scenarios room has empty spaces in message storage
      // so room waits for 50ms to be filled
      const lastIid = R.last(sortedIid)!

      // Note: retry for 20 times in hope for being filled
      if (needsRetry && retryCount > 0) {
        // Note: if room is not fulled get messages from server and give up
        if (retryCount === 1) {
          await getMessages(roomId, {
            iid: lastIid,
            position: 'PREVIOUS',
            count: FETCH_MESSAGES_WINDOW_SIZE,
          })
        }
        //  Race happened; Waiting for hole iid to be received
        else await asyncDelay(50)

        //  Retry handling new messages
        return handleNewMessage(roomMessages, isFromServer, retryCount - 1)
      }
      saveMessages(roomMessages)
      await roomService.handleNewMessages(roomMessages)
      openedRoomMessagesService.handleNewMessages(roomMessages, isFromServer)
      lastOpenedRoomMessagesService.handleNewMessages(roomMessages)
      sharedMediaService.handleNewMessage(roomMessages)
      await sharedMediaService.increaseSharedMediaTotalCount(roomMessages)
      return 0
      //   TODO: deleting pending messages has 1 second delay now
    }),
  )
}

export function saveMessages(messages: Message[]) {
  return messagesStorage.insert(messages)
}

export const isEditMessagePatch = (
  patch: Omit<EventEditMessagePayload, 'iid' | 'roomId'>,
) => {
  return R.has('body')(patch) || R.has('attachment')(patch)
}
export async function saveEditMessage(patches: EventEditMessagePayload[]) {
  openedRoomMessagesService.handleEditPatches(patches)
  lastOpenedRoomMessagesService.editLastOpenedRoomMessages(patches)
  sharedMediaService.handleEditMessages(patches)
  pinMessagesService.handleEditMessages(patches)
  pinRoomApi.handleUpdateIids(R.pluck('iid', patches))
  await Promise.all(
    patches.map(async ({ roomId, iid, ...updatedFields }) =>
      messagesStorage.updateOne([roomId, iid], {
        $set: {
          ...updatedFields,
          ...(isEditMessagePatch(updatedFields) && { 'patches.edited': true }),
        },
      }),
    ),
  )
  const roomIds = R.uniq(patches.map(({ roomId }) => roomId))
  await Promise.all(
    roomIds.map(roomId => updateRoomLastMessageDebounced(roomId)),
  )
}

export async function deleteMessagesFromClient(
  patch: EventDeleteMessagesPayload,
) {
  const { roomId, iids } = patch
  pinMessagesService.handleEditMessages(
    patch.iids.map(iid => ({ roomId, iid, pinned: false })),
  )
  openedRoomMessagesService.handleDeletePatches(patch)
  lastOpenedRoomMessagesService.handleDeletePatches(patch)
  await sharedMediaService.removeSharedMediaMessagesByIid(roomId, iids)
  await messagesStorage.deleteMany(roomId, iids)
  await updateRoomLastMessageDebounced(roomId)
}

export function isMessageFromClientUser(msg?: Message) {
  if (!msg) return false
  // eslint-disable-next-line no-underscore-dangle
  const sender = '_sender' in msg ? msg._sender : msg.sender
  if (!sender) return false // TODO: remove me later
  return sender.type === 'USER' && sender.id === getClientUserId()
}

export const isEditableMessage = (type: string) =>
  ['IMAGE', 'FILE', 'TEXT', 'VOICE', 'VIDEO', 'AUDIO'].includes(type)

export const isSharedMediaMessage = (type: string) =>
  ['IMAGE', 'FILE', 'VIDEO', 'AUDIO', 'VOICE'].includes(type)

export const isDownloadableMessage = (type: string) =>
  ['IMAGE', 'FILE', 'VIDEO', 'AUDIO', 'VOICE'].includes(type)

export const isSharableMessage = (type: string) =>
  ['IMAGE', 'VIDEO', 'AUDIO'].includes(type) // TODO: isSharable must be replaced with isDownloadableMessage

export const getMessageDownloadStatus = async (message: IMessage) => {
  if (!isDownloadableMessage(message?.type)) return { downloadable: false }

  const { filename, fileId } = message.attachment

  return {
    downloadable: true,
    download: () => {
      openFile(getFileUrl(fileId), filename || fileId, IS_IOS)
    },
  }
}

export async function getAvailableFunctions(
  message: IMessage,
): Promise<ImageDetails['functions']> {
  const { roomId, iid } = message
  const { downloadable, download } = await getMessageDownloadStatus(
    message as IMessage,
  )

  const onDownload = async () => {
    download()
    await closeCurrentModal()
  }

  const onForward = async () => {
    await closeCurrentModal()
    openForwardListHandler(roomId, [iid], 'MEDIA_VIEWER_MODAL')
  }

  const onEdit = async () => {
    await closeCurrentModal()
    sendFieldApis.setEditContent(message as WithBodyMessage)
  }

  const canEdit = await canEditMessage(message as Message)

  const onRemove = async () => {
    await closeCurrentModal()
    showDeleteMessageModal(roomId, [iid])
  }

  const canRemove = await canEditMessage(message as Message)

  return {
    onDownload: downloadable ? onDownload : null,
    onEdit: canEdit ? onEdit : null,
    onShowSharedMedia: null,
    onRemove: canRemove ? onRemove : null,
    onForward,
    onShare: null,
  }
}

export async function removeMessagesByRange(
  roomId: string,
  start: number,
  end: number,
) {
  messagesStorage.removeByRange(
    IDBKeyRange.bound([roomId, start], [roomId, end]),
  )
  if (roomId === getOpenedRoomId())
    openedRoomMessagesService.clearOpenedRoomMessages()
}

export function searchMessages({
  roomId,
  debouncedSearchTerm,
}: {
  roomId: string
  debouncedSearchTerm: string
}) {
  return request<SearchMessages>(
    reqSearchMessages({ roomId, query: debouncedSearchTerm }),
  )
}

export function canCopyMessage(message: Message) {
  return message.type === 'CONTACT' || (message as WithBodyMessage)?.body
}

export async function canSendMessage(roomId: string) {
  const room = await getRoom(roomId)
  if (!room) return false
  if (isHall(room.type)) {
    const memberPermissions = await decodeSubscriptionPermissions(roomId)
    return memberPermissions.sendMessage
  }
  if (room.type === 'WAPP_ROOM') return false
  if (room.type === 'PV') {
    const pv = await getPv(roomId)
    const user = await getUser(pv.to)
    if (user.deletedAt) return false
    if (user.isBlocked) return false
    const roomPermissions = await openedRoomPermissions()
    return (
      roomPermissions.sendMessage ||
      roomPermissions.sendMedia ||
      roomPermissions.sendWapp
    )
  }
  return true
}

export async function canEditMessage(message: Message) {
  if (!isEditableMessage(message.type)) return false
  if (message.forwardedFrom) return false
  const room = await getRoom(message.roomId)
  const isFromClientUser = isMessageFromClientUser(message)
  if (isHall(room.type)) {
    // NOTE: If a hall is removed, all rights are revoked.
    const permissions = await decodeSubscriptionPermissions(room.id)
    return (
      permissions.editMessage || (isFromClientUser && permissions.sendMessage)
    )
  }
  return isFromClientUser
}

export async function isClientUserAdmin(message: Message) {
  const room = await getRoom(message.roomId)
  if (room && isHall(room.type)) {
    const permissions = await decodeSubscriptionPermissions(room.id)
    return permissions.isAdmin
  }
}

const DEFAULT_PERMISSIONS = {
  canEditWapp: false,
  canDeleteWapp: false,
  canShareWapp: false,
  onEditWapp: nothing,
  onDeleteWapp: nothing,
  onShareWapp: nothing,
}
export async function getPermissionOnWappMessage(
  message: Message | undefined,
): Promise<{
  canEditWapp: boolean
  canDeleteWapp: boolean
  canShareWapp: boolean
  onEditWapp: () => unknown
  onDeleteWapp: () => unknown
  onShareWapp: () => unknown
}> {
  if (!message || message.type !== 'WAPP' || !message.attachment)
    return DEFAULT_PERMISSIONS

  const { wappId: messageWappId, wisId: messageWisId } = message.attachment
  const { customActions, wappSessionId } =
    getWappSessionByWappIdAndWisId(messageWappId, messageWisId) || {}

  if (!wappSessionId) return DEFAULT_PERMISSIONS

  return {
    canEditWapp: Boolean(customActions?.includes(CustomAction.EDIT)),
    onEditWapp: () => sendCustomActionToWapp(wappSessionId, CustomAction.EDIT),
    canDeleteWapp: Boolean(customActions?.includes(CustomAction.DELETE)),
    onDeleteWapp: () =>
      sendCustomActionToWapp(wappSessionId, CustomAction.DELETE),
    canShareWapp: Boolean(customActions?.includes(CustomAction.SHARE)),
    onShareWapp: () =>
      sendCustomActionToWapp(wappSessionId, CustomAction.SHARE),
  }
}

export async function canDeleteMessage(message: Message) {
  if (isMessageFromClientUser(message)) return true
  const room = await getRoom(message.roomId)
  if (!room) return false

  if (isHall(room.type)) {
    // NOTE: If a hall is removed, all rights are revoked.
    const permissions = await decodeSubscriptionPermissions(room.id)
    return permissions.deleteMessages
  }

  return (
    (room.type === 'PV' || room.type === 'WAPP_ROOM') &&
    message.type === 'EVENT'
  )
}

export function canForwardMessage(message: Message) {
  return message.type !== 'EVENT' && message.type !== 'CALL'
}

export function canPinMessage(message: Message) {
  return message.type !== 'EVENT' && message.type !== 'CALL'
}

export async function openForwardListHandler(
  sourceRoomId: string,
  iids: number[],
  source: string,
) {
  // TODO: To use line below, Fix updating cached permissions in forward menu
  // if (isDesktopSize()) return await showForwardMenuModal(sourceRoomId, iids)
  await openForwardList(
    (roomIds, caption) => {
      analite('MESSAGE_FORWARD', { multiple: iids.length > 1, source })
      forwardMessages(roomIds, { roomId: sourceRoomId, iids }, caption).then(
        () =>
          showNMessageForwardedToChatSnackbar(roomIds.length, iids.length > 1),
      )
    },
    undefined,
    destRoomId =>
      closeForwardList().then(async () => {
        if (destRoomId !== sourceRoomId) await openRoom(destRoomId)
        const isPublic = await isPublicHall(sourceRoomId)
        setTimeout(
          () => sendFieldApis.setForwardContent(sourceRoomId, iids, isPublic),
          0,
        )
      }),
  )
}

export async function getMessagesByCategoryAndRange(
  roomId: string,
  category: CategoryType,
  upperIid: number,
  lowerIid?: number,
): Promise<{ messages: Message[]; fetchedAll: boolean }> {
  if (!upperIid) return { messages: [], fetchedAll: false }
  if (lowerIid) {
    if (upperIid < lowerIid) return { messages: [], fetchedAll: false }
    let fetchedMessages = [] as Message[]
    let tempUpperIid = upperIid
    let tempFetchedAll = false
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { messages, fetchedAll } = await getMessagesByCategory(
        roomId,
        category,
        tempUpperIid,
      )
      const filteredMessages = messages.filter(
        message => message.iid > lowerIid,
      )
      fetchedMessages = R.concat(fetchedMessages, filteredMessages)
      if (fetchedAll) {
        tempFetchedAll = true
        break
      }
      if (R.last(messages).iid < lowerIid || fetchedAll) break
      tempUpperIid = R.last(messages).iid
    }
    return { messages: fetchedMessages, fetchedAll: tempFetchedAll }
  }
  return getMessagesByCategory(roomId, category, upperIid)
}

export async function getSenderName(sender: MessageSender, fullName = false) {
  if (sender.type === 'USER') {
    const user = await getUser(sender.id)
    if (fullName) return getUserDisplayName(user)
    return getUserFirstName(user)
  }
  return sender.type === 'CHANNEL'
    ? (await getHall(sender.id))?.name
    : (await getWapp(sender.id))?.name
}

export function handleDeleteMessagesModal(
  messages: Message[],
  canDeleteWappsWithinSelectedMessages: boolean,
  roomId: string,
) {
  const wappMessages = messages.filter(message => message.type === 'WAPP')
  const selectedItems = messages.map(msg => msg.iid)
  if (wappMessages.length > 0 && canDeleteWappsWithinSelectedMessages) {
    showDeleteContentFromInlineConfirmModal(roomId, checked => {
      if (checked) {
        wappMessages.map(async wappMessage => {
          const { onDeleteWapp } = await getPermissionOnWappMessage(wappMessage)
          onDeleteWapp()
        })
        // should wait until wapp handle delete content
        setTimeout(() => {
          analite('CHAT_MESSAGE_DELETE')
          deleteMessages(roomId, selectedItems)
        }, 500)
      } else {
        analite('CHAT_MESSAGE_DELETE')
        deleteMessages(roomId, selectedItems)
      }
    })
  } else showDeleteMessageModal(roomId, selectedItems)
}

export async function sendInviteLinkToReceivers(
  receiverIds: string[],
  inviteLink: string,
  isGroup: boolean,
) {
  const sender = getClientUser()

  const body = t('sendInviteLink.inviteText')(
    getUserDisplayName(sender),
    isGroup,
    inviteLink,
  )
  await Promise.all(
    receiverIds.map(async receiverId => {
      const pv = await getPvByUserId(receiverId)
      if (!pv) return
      const room = await getRoom(pv.roomId)
      if (!room) return
      sendTextMessage(body, undefined, room.id, 'PV')
    }),
  )
  showInviteLinkSentSuccessfullySnackbar()
}
